import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import RealtimeKitClient from "@cloudflare/realtimekit";
import { useAudioLevels } from "@/hooks/useAudioLevels";
import { initialSpeakerState, stepSpeaker } from "@/lib/speakerSwitch";

/**
 * Initialise a Cloudflare RealtimeKit client exactly once per authToken and
 * (optionally) join the room. Survives React StrictMode double effects: the
 * in-flight init promise is memoised per token at module level, so concurrent
 * callers share one client ("Unsupported concurrent calls on Client.init").
 *
 * Returns { meeting, joined, error, leave }.
 */
const clientCache = new Map(); // cacheKey -> { promise, meeting, joined }

// Picture-quality profiles. Each caps the simulcast layers so a phone on venue Wi‑Fi / LTE never
// pushes more than roughly: saver ≈0.9 Mbps, auto ≈2.3 Mbps, sharp ≈4.4 Mbps. The SFU hands the
// half/quarter layer to a congested receiver instead of stalling the full one.
export const QUALITY = {
  saver: {
    label: "Data saver", sub: "480p · weak Wi‑Fi or LTE", height: 480,
    media: { width: { ideal: 854 }, height: { ideal: 480 }, frameRate: { ideal: 20 } },
    encodings: [
      { rid: "f", scaleResolutionDownBy: 1, maxBitrate: 550_000, maxFramerate: 20 },
      { rid: "h", scaleResolutionDownBy: 2, maxBitrate: 250_000, maxFramerate: 20 },
      { rid: "q", scaleResolutionDownBy: 4, maxBitrate: 100_000, maxFramerate: 12 },
    ],
  },
  auto: {
    label: "Auto", sub: "720p · recommended", height: 720,
    media: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 24 } },
    encodings: [
      { rid: "f", scaleResolutionDownBy: 1, maxBitrate: 1_600_000, maxFramerate: 24 },
      { rid: "h", scaleResolutionDownBy: 2, maxBitrate: 550_000, maxFramerate: 24 },
      { rid: "q", scaleResolutionDownBy: 4, maxBitrate: 180_000, maxFramerate: 15 },
    ],
  },
  sharp: {
    label: "Sharp", sub: "1080p · strong Wi‑Fi, newer phone", height: 1080,
    media: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 24 } },
    encodings: [
      { rid: "f", scaleResolutionDownBy: 1, maxBitrate: 3_200_000, maxFramerate: 24 },
      { rid: "h", scaleResolutionDownBy: 2, maxBitrate: 900_000, maxFramerate: 24 },
      { rid: "q", scaleResolutionDownBy: 4, maxBitrate: 250_000, maxFramerate: 15 },
    ],
  },
};

// Microphone handling. RealtimeKit's default is 32 kbps mono Opus with the browser's echo
// cancellation / noise suppression / auto-gain all on — fine for a phone call, but it makes a
// room microphone sound thin and metallic. "studio" (host laptop, cameras: they never play
// remote audio, so no echo path) sends 64 kbps and leaves the mic signal untouched;
// "phone" (guests listening on their own device) keeps the processing but also gets 64 kbps.
export const AUDIO_PROFILE = {
  studio: { enableHighBitrate: true, enableStereo: false, echoCancellation: false, noiseSupression: false, autoGainControl: false },
  phone: { enableHighBitrate: true, enableStereo: false, echoCancellation: true, noiseSupression: true, autoGainControl: true },
};

function getOrInitClient(cacheKey, authToken, defaults, quality, audioProfile) {
  let entry = clientCache.get(cacheKey);
  if (!entry) {
    const q = QUALITY[quality] || QUALITY.auto;
    entry = { meeting: null, joinPromise: null };
    entry.promise = RealtimeKitClient.init({
      authToken,
      defaults: { ...defaults, mediaConfiguration: { video: q.media, audio: AUDIO_PROFILE[audioProfile] || AUDIO_PROFILE.phone } },
      overrides: { simulcastConfig: { encodings: q.encodings } },
    }).then((m) => { entry.meeting = m; return m; });
    clientCache.set(cacheKey, entry);
  }
  return entry;
}

/**
 *  quality       "saver" | "auto" | "sharp" (see QUALITY)
 *  audioProfile  "studio" | "phone" (see AUDIO_PROFILE)
 *  session       bump to force a brand-new client for the same token (reconnect, quality change)
 */
export function useRtkMeeting(authToken, { audio = true, video = true, autoJoin = true, quality = "auto", audioProfile = "phone", session = 0 } = {}) {
  const [meeting, setMeeting] = useState(null);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const cacheKey = authToken ? `${authToken}|${session}|${quality}|${audioProfile}` : null;

  useEffect(() => {
    mountedRef.current = true;
    if (!cacheKey) return undefined;
    const entry = getOrInitClient(cacheKey, authToken, { audio, video }, quality, audioProfile);
    let onJoined, onLeft, m;
    (async () => {
      try {
        m = await entry.promise;
        if (!mountedRef.current) return;
        if (typeof window !== "undefined") window.__fcMeeting = m; // debugging aid
        onJoined = () => setJoined(true);
        onLeft = () => setJoined(false);
        m.self.on("roomJoined", onJoined);
        m.self.on("roomLeft", onLeft);
        setMeeting(m);
        if (m.self.roomJoined) setJoined(true);
        else if (autoJoin) {
          if (!entry.joinPromise) entry.joinPromise = m.join();
          await entry.joinPromise;
          if (mountedRef.current) setJoined(true);
        }
      } catch (e) {
        if (mountedRef.current) setError(e?.message || "Could not connect to the studio");
      }
    })();
    return () => {
      mountedRef.current = false;
      if (m && onJoined) { try { m.self.off("roomJoined", onJoined); m.self.off("roomLeft", onLeft); } catch (e) { /* noop */ } }
      // Leave shortly after unmount unless we're re-mounted (StrictMode replays effects).
      setTimeout(() => {
        if (mountedRef.current) return;
        const cur = clientCache.get(cacheKey);
        if (cur?.meeting) { try { cur.meeting.leave(); } catch (e) { /* noop */ } }
        clientCache.delete(cacheKey);
      }, 250);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const leave = useCallback(async () => {
    const cur = clientCache.get(cacheKey);
    clientCache.delete(cacheKey);
    if (cur?.meeting) { try { await cur.meeting.leave(); } catch (e) { /* noop */ } }
    setJoined(false);
  }, [cacheKey]);

  return { meeting, joined, error, leave };
}

/**
 * Reactive roster of everyone in the room (self + remote), keyed by our
 * customParticipantId (= show participant_id). Re-renders on join/leave and
 * on media state changes.
 */
export function useRoster(meeting) {
  const [tick, setTick] = useState(0);
  const bump = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!meeting) return undefined;
    const joinedMap = meeting.participants.joined;
    const perPeer = new Map();
    const attach = (p) => {
      if (!p || perPeer.has(p.id)) return;
      const h = () => bump();
      p.on("videoUpdate", h);
      p.on("audioUpdate", h);
      perPeer.set(p.id, { p, h });
    };
    const detach = (p) => {
      const e = perPeer.get(p?.id);
      if (!e) return;
      try { e.p.off("videoUpdate", e.h); e.p.off("audioUpdate", e.h); } catch (err) { /* noop */ }
      perPeer.delete(p.id);
    };
    const onJoin = (p) => { attach(p); bump(); };
    const onLeft = (p) => { detach(p); bump(); };
    const onSelf = () => bump();
    joinedMap.on("participantJoined", onJoin);
    joinedMap.on("participantLeft", onLeft);
    joinedMap.on("participantsUpdate", bump);
    meeting.self.on("videoUpdate", onSelf);
    meeting.self.on("audioUpdate", onSelf);
    meeting.self.on("roomJoined", onSelf);
    meeting.self.on("deviceUpdate", onSelf);
    try { joinedMap.toArray().forEach(attach); } catch (e) { /* noop */ }
    bump();
    return () => {
      try {
        joinedMap.off("participantJoined", onJoin);
        joinedMap.off("participantLeft", onLeft);
        joinedMap.off("participantsUpdate", bump);
        meeting.self.off("videoUpdate", onSelf);
        meeting.self.off("audioUpdate", onSelf);
        meeting.self.off("roomJoined", onSelf);
        meeting.self.off("deviceUpdate", onSelf);
      } catch (e) { /* noop */ }
      perPeer.forEach((e) => { try { e.p.off("videoUpdate", e.h); e.p.off("audioUpdate", e.h); } catch (err) { /* noop */ } });
      perPeer.clear();
    };
  }, [meeting, bump]);

  if (!meeting) return { byCustomId: new Map(), list: [], tick };
  const list = [];
  const byCustomId = new Map();
  const push = (p, isSelf) => {
    const entry = {
      peerId: p.id,
      customId: p.customParticipantId || null,
      name: p.name,
      isSelf,
      videoEnabled: !!p.videoEnabled,
      audioEnabled: !!p.audioEnabled,
      presetName: p.presetName,
      rtk: p,
    };
    list.push(entry);
    if (entry.customId) byCustomId.set(entry.customId, entry);
  };
  try {
    if (meeting.self?.roomJoined) push(meeting.self, true);
    meeting.participants.joined.toArray().forEach((p) => push(p, false));
  } catch (e) { /* noop */ }
  // A phone that reconnected can linger twice for a while (old peer + new). Keep the live one.
  const dupes = new Map();
  list.forEach((e) => { if (e.customId) dupes.set(e.customId, (dupes.get(e.customId) || 0) + 1); });
  const deduped = [...dupes.values()].some((n) => n > 1)
    ? list.filter((e) => {
      if (!e.customId || dupes.get(e.customId) === 1) return true;
      const rivals = list.filter((o) => o.customId === e.customId);
      const score = (o) => (o.isSelf ? 4 : 0) + (o.videoEnabled ? 2 : 0) + (o.audioEnabled ? 1 : 0);
      const best = rivals.reduce((a, b) => (score(b) > score(a) ? b : score(b) === score(a) && rivals.indexOf(b) > rivals.indexOf(a) ? b : a));
      return best === e;
    })
    : list;
  deduped.forEach((e) => { if (e.customId) byCustomId.set(e.customId, e); });
  return { byCustomId, list: deduped, tick };
}

/** Active speaker peerId (for the auto "speaker" layout). */
export function useActiveSpeaker(meeting) {
  const [peerId, setPeerId] = useState(null);
  useEffect(() => {
    if (!meeting) return undefined;
    const h = ({ peerId: id }) => setPeerId(id);
    meeting.participants.on("activeSpeaker", h);
    return () => { try { meeting.participants.off("activeSpeaker", h); } catch (e) { /* noop */ } };
  }, [meeting]);
  return peerId;
}

/**
 * Smoothed speaker for the auto-speaker layout (see lib/speakerSwitch.js for the rules):
 * coughs don't cut (confirm), shots are held (program.speaker.hold), room cameras (Owl — hears
 * the whole room) never trigger a cut, and after silence the room camera becomes the wide shot.
 * Same algorithm runs in the console preview and the cloud recorder.
 */
export function useStableSpeaker(meeting, roster, participants, program) {
  const raw = useActiveSpeaker(meeting);
  const enabled = program?.layout === "speaker";
  const levels = useAudioLevels(meeting, roster, !!meeting && enabled);
  const [stable, setStable] = useState(null);
  const st = useRef(initialSpeakerState());
  const holdMs = Math.max(500, (program?.speaker?.hold ?? 3) * 1000);
  const wideFallback = program?.speaker?.wide_fallback !== false;
  const roomCams = useMemo(() => new Set((participants || []).filter((p) => p.room_cam).map((p) => p.participant_id)), [participants]);
  const live = useRef({ raw, levels, roster });
  live.current = { raw, levels, roster };

  useEffect(() => {
    if (!enabled) { st.current = initialSpeakerState(); setStable(null); return undefined; }
    st.current = initialSpeakerState(Date.now());
    const tick = () => {
      const { raw: spk, levels: lv, roster: r } = live.current;
      const cur = stepSpeaker(st.current, { list: r?.list || [], roomCams, raw: spk, levels: lv, holdMs, wideFallback }, Date.now());
      setStable((prev) => (prev === cur ? prev : cur));
    };
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [enabled, holdMs, wideFallback, roomCams]);

  return stable;
}

/** 0–10 media score → "good" | "fair" | "poor" (RealtimeKit flags < 5 as a poor connection). */
export function healthLevel(score) {
  if (score == null || Number.isNaN(score)) return null;
  if (score >= 7) return "good";
  if (score >= 5) return "fair";
  return "poor";
}

/**
 * What our own camera is really sending: polls the video producer's WebRTC stats every 3 s.
 * Returns { height, fps, kbps, reason } — reason is Chrome's qualityLimitationReason
 * ("bandwidth" = connection is the bottleneck, "cpu" = device is struggling).
 */
export function useUplinkStats(meeting, enabled = true) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (!meeting || !enabled) return undefined;
    let prev = null;
    let alive = true;
    const poll = async () => {
      try {
        const producer = (meeting.self.producers || []).find((p) => p.kind === "video" && !p.closed);
        if (!producer || !meeting.self.videoEnabled) { if (alive) setStats(null); return; }
        const report = await producer.getStats();
        let best = null;
        let bytes = 0;
        report.forEach((r) => {
          if (r.type !== "outbound-rtp" || r.kind !== "video") return;
          bytes += r.bytesSent || 0;
          if (r.framesPerSecond && (!best || (r.frameHeight || 0) > (best.frameHeight || 0))) best = r;
        });
        const now = performance.now();
        const kbps = prev && now > prev.t ? Math.round(((bytes - prev.bytes) * 8) / (now - prev.t)) : null;
        prev = { bytes, t: now };
        if (!alive) return;
        setStats(best ? { height: best.frameHeight || 0, fps: Math.round(best.framesPerSecond || 0), kbps, reason: best.qualityLimitationReason || "none" } : { height: 0, fps: 0, kbps, reason: "starting" });
      } catch (e) { /* stats unavailable on this browser */ }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [meeting, enabled]);
  return stats;
}

/**
 * Combine the SDK score (arrives every ~7 s, occasionally never for a consumer) with what is
 * really flowing: frames reaching a remote tile (flow) or the layer we are sending (uplink).
 * A frozen picture / starved uplink always wins over a rosy score.
 */
export function combinedHealth(entry, flow, uplink) {
  const fresh = entry && Date.now() - entry.at < 30000;
  if (flow && flow.stalledMs > 2500) return { level: "poor", detail: "picture frozen" };
  const parts = [];
  let level = fresh ? healthLevel(entry.score) : null;
  if (fresh) parts.push(`${Math.round(entry.score * 10) / 10}/10`);
  if (flow) {
    const fl = flow.fps >= 10 ? "good" : flow.fps >= 4 ? "fair" : "poor";
    level = worst(level, fl);
    parts.push(`${Math.round(flow.fps)} fps in`);
  }
  if (uplink && uplink.height) {
    const ul = uplink.height >= 540 ? "good" : uplink.height >= 300 ? "fair" : "poor";
    level = worst(level, ul);
    parts.push(`sending ${uplink.height}p @ ${uplink.fps} fps${uplink.kbps ? ` · ${uplink.kbps >= 1000 ? `${(uplink.kbps / 1000).toFixed(1)} Mbps` : `${uplink.kbps} kbps`}` : ""}`);
    if (uplink.reason === "bandwidth") parts.push("limited by the connection");
    if (uplink.reason === "cpu") parts.push("device is struggling (CPU)");
  }
  return { level, detail: parts.join(" · ") || null };
}

const RANK = { good: 0, fair: 1, poor: 2 };
function worst(a, b) {
  if (!a) return b;
  if (!b) return a;
  return RANK[a] >= RANK[b] ? a : b;
}

/**
 * Per-peer connection health from RealtimeKit's call stats: remote peers report the
 * quality of the video we RECEIVE from them; self reports the quality of our UPLOAD.
 * Returns { [peerId]: { score, kind, at } } (video scores win over audio when both exist).
 */
export function useConnectionHealth(meeting) {
  const [health, setHealth] = useState({});
  useEffect(() => {
    if (!meeting) return undefined;
    const upd = (peerId) => ({ kind, score, isScreenshare }) => {
      if (isScreenshare || !peerId) return;
      setHealth((h) => {
        const cur = h[peerId];
        if (cur && cur.kind === "video" && kind !== "video" && Date.now() - cur.at < 15000) return h;
        return { ...h, [peerId]: { score, kind, at: Date.now() } };
      });
    };
    const onRemote = (p) => upd(p.participantId)(p);
    const onSelf = (p) => upd(meeting.self.id)(p);
    const onLeft = (p) => setHealth((h) => { if (!h[p.id]) return h; const n = { ...h }; delete n[p.id]; return n; });
    meeting.participants.on("mediaScoreUpdate", onRemote);
    meeting.participants.joined.on("participantLeft", onLeft);
    meeting.self.on("mediaScoreUpdate", onSelf);
    return () => {
      try {
        meeting.participants.off("mediaScoreUpdate", onRemote);
        meeting.participants.joined.off("participantLeft", onLeft);
        meeting.self.off("mediaScoreUpdate", onSelf);
      } catch (e) { /* noop */ }
    };
  }, [meeting]);
  return health;
}

export default useRtkMeeting;
